import { useEffect, useState } from "react";
import { Button, Input, Modal, DialogFooter, Loader } from "@/ui/atoms";
import { FormField } from "@/ui/molecules";

export type TSegmentValueDraft = {
    code: string;
    description: string;
    yardi_segment_value: string;
    is_active: boolean;
};

export type TSegmentValueFormModalProps = {
    open: boolean;
    mode: "create" | "edit";
    segmentNumber: number;
    initial?: TSegmentValueDraft;
    isSaving?: boolean;
    error?: string | null;
    onSubmit: (draft: TSegmentValueDraft) => void;
    onClose: () => void;
};

const emptyDraft = (): TSegmentValueDraft => ({
    code: "",
    description: "",
    yardi_segment_value: "",
    is_active: true,
});

export function SegmentValueFormModal({
    open,
    mode,
    segmentNumber,
    initial,
    isSaving = false,
    error,
    onSubmit,
    onClose,
}: TSegmentValueFormModalProps) {
    const [draft, setDraft] = useState<TSegmentValueDraft>(initial ?? emptyDraft());

    // Re-seed the form whenever the modal (re)opens for a different row.
    useEffect(() => {
        if (open) setDraft(initial ?? emptyDraft());
    }, [open, initial]);

    // `code` falls back to the platform value when blank (matches legacy create).
    const platformValue = draft.yardi_segment_value.trim();
    const resolvedCode = draft.code.trim() || platformValue;
    const isValid = resolvedCode !== "";

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValid) return;
        onSubmit({
            code: resolvedCode,
            description: draft.description.trim(),
            yardi_segment_value: platformValue,
            is_active: draft.is_active,
        });
    };

    return (
        <Modal
            open={open}
            onOpenChange={(next) => !next && onClose()}
            title={
                mode === "create"
                    ? `Add Segment ${segmentNumber} Value`
                    : `Edit Segment ${segmentNumber} Value`
            }
            description="Platform value and status."
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                        {error}
                    </div>
                )}
                <FormField label="Platform Value" htmlFor="seg-platform-value" required>
                    <Input
                        id="seg-platform-value"
                        aria-label="Platform Value"
                        value={draft.yardi_segment_value}
                        onChange={(e) =>
                            setDraft({ ...draft, yardi_segment_value: e.target.value })
                        }
                        placeholder="Platform Value"
                    />
                </FormField>
                <FormField label="Description" htmlFor="seg-description">
                    <Input
                        id="seg-description"
                        aria-label="Description"
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        placeholder="Description"
                    />
                </FormField>
                <label className="inline-flex items-center gap-2">
                    <input
                        type="checkbox"
                        aria-label="Active"
                        checked={draft.is_active}
                        onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                </label>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSaving || !isValid}>
                        {isSaving ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Saving...
                            </>
                        ) : mode === "create" ? (
                            "Create"
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Modal>
    );
}
